"use client";

import {
  cloneElement,
  isValidElement,
  type ReactElement,
  useState,
} from "react";
import { cn } from "@/lib/utils";
import type { LegendSelectSource } from "../use-legend-click-sync";
import {
  type LegendItemData,
  LegendItemProvider,
  LegendProvider,
} from "./legend-context";

export interface LegendProps {
  /** Legend items data */
  items: LegendItemData[];
  /** Controlled hover state */
  hoveredIndex?: number | null;
  /** Hover state change callback */
  onHoverChange?: (index: number | null) => void;
  /** Controlled selected/active state (persistent). */
  selectedIndex?: number | null;
  /** Called when a legend item is clicked. Sender is expected to toggle. */
  onSelect?: (index: number, source?: LegendSelectSource) => void;
  /** Ref setter for each item; used to auto-scroll into view when selected. */
  onLabelRef?: (index: number) => (el: HTMLElement | null) => void;
  /** Title shown above the legend */
  title?: string;
  /** Title class name */
  titleClassName?: string;
  /** Container class name */
  className?: string;
  /** Children - should contain a single LegendItem that will be mapped for each item */
  children: ReactElement;
}

export function Legend({
  items,
  hoveredIndex: controlledHoveredIndex,
  onHoverChange,
  selectedIndex = null,
  onSelect,
  onLabelRef,
  title,
  titleClassName = "text-sm font-medium",
  className = "",
  children,
}: LegendProps) {
  const [internalHoveredIndex, setInternalHoveredIndex] = useState<
    number | null
  >(null);

  const isControlled = controlledHoveredIndex !== undefined;
  const hoveredIndex = isControlled
    ? controlledHoveredIndex
    : internalHoveredIndex;
  const setHoveredIndex = (index: number | null) => {
    if (isControlled) {
      onHoverChange?.(index);
    } else {
      setInternalHoveredIndex(index);
    }
  };

  const handleSelect = (index: number) => {
    onSelect?.(index, {
      kind: "legend-item",
      id: items[index]?.id ?? null,
    });
  };

  const contextValue = {
    items,
    hoveredIndex,
    setHoveredIndex,
    selectedIndex,
    handleSelect,
    getLabelRef: onLabelRef,
  };

  return (
    <LegendProvider value={contextValue}>
      <div className={cn(className)}>
        {title && (
          <h3 className={cn("mb-1 text-legend-foreground", titleClassName)}>
            {title}
          </h3>
        )}
        {items.map((item, index) => {
          const isHovered = hoveredIndex === index;
          const isFaded = hoveredIndex !== null && hoveredIndex !== index;
          const isSelected = selectedIndex === index;
          const percentage =
            typeof item.value === "number" && item.maxValue
              ? (item.value / item.maxValue) * 100
              : (item.percentage ?? 0);

          const itemContext = {
            item,
            index,
            isHovered,
            isFaded,
            isSelected,
            percentage,
          };

          if (isValidElement(children)) {
            return (
              <LegendItemProvider key={item.label} value={itemContext}>
                {cloneElement(children)}
              </LegendItemProvider>
            );
          }

          return null;
        })}
      </div>
    </LegendProvider>
  );
}

Legend.displayName = "Legend";
